import { AmountSuperScript } from "components/design/AmountSuperScript";
import ConditionalToolTip from "components/design/conditionalToolTip";
import { Divider } from "components/design/Divider";
import { TableUI } from "components/design/TableUI";
import ToolTip from "components/design/toolTip";
import { AnimatedNumbers } from "components/DesignSystem/AnimatedNumbers/AnimatedNumbers";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { Avatar } from "components/DesignSystem/AvatarGroup/Avatar";
import { Button } from "components/DesignSystem/Button/Button";
import { Checkbox } from "components/DesignSystem/Checkbox/Checkbox";
import { Chip } from "components/DesignSystem/Chips/Chips";
import { Filter } from "components/DesignSystem/Filter/Filter";
import Loader from "components/DesignSystem/Loader/loader";
import Modal from "components/DesignSystem/Modal/Modal";
import { PaginationHandler } from "components/DesignSystem/Pagination/Pagination";
import RadioGroup from "components/DesignSystem/RadioGroup/RadioGroup";
import { Search } from "components/DesignSystem/Search/Search";
import Table from "components/DesignSystem/Table/V2/Table";
import { BooksDateFilter } from "components/Filters/BooksDateFilter";
import {
  DiscountTransactions,
  initialTransaction,
} from "components/LinkInkleInvoiceModal/LinkInkleInvoiceModal";
import { SuggestedBillsAccordion } from "components/LinkInkleBillModal/SuggestedBillsAccordion";
import { RadioCard } from "components/RadioCard/RadioCard";
import { InfoItem } from "components/Transaction/Slider/TransactionSlider";
import { billColumns } from "components/VendorBills/BillList";
import {
  PAY_PARTIALLY,
  RESOLVE_DIFFERENCE,
} from "constants/invoiceResolveOptions";
import {
  EXCHANGE_GAIN_OR_LOSS_IDENTIFIER,
  INCOME_INDENTIFIER,
  WITHHELD_TAX_PAYABLE_IDENTIFIER,
} from "constants/categoryIdentifier";
import { USD } from "constants/currency";
import { YYYY_MM_DD, DISPLAY_DATE } from "constants/date";
import { FINANCIAL_RECORD_TYPE } from "constants/vendorBills";
import dayjs from "dayjs";
import { Form, Formik, useFormikContext } from "formik";
import { linkInkleSchema } from "formValidations/linkInvoiceSchema";
import { useChartOfAccounts } from "hooks/useChartOfAccounts";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useFilters } from "hooks/useFilter";
import { useModal } from "hooks/useModal";
import { useToast } from "hooks/useToast";
import randomBytes from "randombytes";
import { useMemo, useState } from "react";
import {
  createColumnHelper,
  getCoreRowModel,
  Row,
  useReactTable,
} from "react-table-8.10.7";
import EmptyInvoices from "static/images/EmptyInvoices.svg";
import {
  useGetTransactionDataQuery,
  useMapInkleTransactionToBillMutation,
} from "store/apis/transactions";
import { useGetAllBillsQuery } from "store/apis/vendorBills";
import { Transactions } from "types/Models/books";
import { VendorBill } from "types/Models/vendorBills";
import { BackendError } from "types/utils/error";
import { ModalProps } from "types/utils/modal";
import { debounce } from "utils/debouncing";
import { flattenTypes } from "utils/flattenCOA";
import { formatDate } from "utils/formatDate";
import { getDateRange } from "utils/getDateRange";
import { getUsdPendingAmount } from "utils/getUsdPendingAmount";
import { pluralize } from "utils/pluralize";
import { roundToFixedDecimal } from "utils/roundtoFixedDecimal";

type InternalTransaction = {
  id: string;
  merchant: string;
  category: string;
  description: string;
  amount: number;
  withheldBillId?: string;
};

type LinkedBillsFormikValues = {
  search?: string;
  transactions: InternalTransaction[];
  resolve_difference?: boolean;
  pay_partially?: boolean;
};

const createColumn = createColumnHelper<VendorBill>();

const MODAL_BODY_LOADER = (
  <div className="t-h-60 t-flex t-justify-center t-items-center">
    <Loader size="small" />
  </div>
);

const LinkInkleBillBody = ({
  transaction: { transaction, available_amount: pending_amount },
  selectedBills,
  setSelectedBills,
  resolvePartialPaymentStep,
}: {
  transaction: Transactions;
  selectedBills: VendorBill[];
  setSelectedBills: React.Dispatch<React.SetStateAction<VendorBill[]>>;
  resolvePartialPaymentStep: boolean;
}) => {
  const group = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const [currentPage, setCurrentPage] = useState(1);
  const [displayCurrency, setDisplayCurrency] = useState<
    "BILL_CURRENCY" | "USD"
  >("USD");

  const { values: formikValues, setFieldValue } =
    useFormikContext<LinkedBillsFormikValues>();

  const { chartOfAccounts = [] } = useChartOfAccounts({
    hiddenCategoryTypes: ["BANK_ACCOUNT"],
  });
  const flattenedTypes = flattenTypes({ accounts: chartOfAccounts });
  const incomeCategory = flattenedTypes.find(
    ({ identifier }) => identifier === INCOME_INDENTIFIER
  );
  const exchangeLossOrGain = flattenedTypes.find(
    ({ identifier }) => identifier === EXCHANGE_GAIN_OR_LOSS_IDENTIFIER
  );

  const defaultDateRange = getDateRange("last90days");
  const { values: filterValues, updateFilter } = useFilters({
    initialValue: {
      START_DATE: dayjs(defaultDateRange.startDate).format(DISPLAY_DATE),
      END_DATE: dayjs(defaultDateRange.endDate).format(DISPLAY_DATE),
      SELECT_PERIOD: "last90days",
    },
  });

  const {
    data: allBills,
    isLoading,
    isSuccess,
  } = useGetAllBillsQuery(
    {
      groupId: group?.uuid!,
      entityId,
      bill_type: FINANCIAL_RECORD_TYPE.BILL,
      search_term: formikValues?.search || null,
      startDate: filterValues.START_DATE
        ? dayjs(filterValues.START_DATE).format(YYYY_MM_DD)
        : undefined,
      endDate: filterValues.END_DATE
        ? dayjs(filterValues.END_DATE).format(YYYY_MM_DD)
        : undefined,
      page_num: currentPage,
      limit: 5,
      linkedStatus: "UNLINKED",
    },
    { skip: !group?.uuid || !entityId, refetchOnMountOrArgChange: true }
  );

  const { bills = [] } = allBills || {};

  const paginationData = {
    totalPage: allBills?.total_pages!,
    currentPage: allBills?.current_page!,
    itemsPerPage: allBills?.per_page!,
    totalItemCount: allBills?.total_count!,
  };

  const transactionAmount = formikValues.transactions.reduce(
    (acc, { amount }) => {
      return acc + Number(amount);
    },
    0
  );

  const billsTotalAmount = selectedBills.reduce(
    (acc, bill) => acc + getUsdPendingAmount(bill),
    0
  );

  const pendingAmount =
    roundToFixedDecimal({
      numberToRound: Math.abs(pending_amount || transaction.amount),
    }) -
    (billsTotalAmount + transactionAmount);

  const pendingAmountWithoutManual = pendingAmount + transactionAmount;

  const enableSelection = pendingAmount > 0;

  const isAnySelectedBillMultiCurrency = selectedBills.some(
    ({ payment_record_amount }) =>
      payment_record_amount?.exchange_currency &&
      payment_record_amount?.exchange_currency !== USD
  );

  const overpayment = pendingAmountWithoutManual < 0;

  const onResolveValueChange = (value: string) => {
    setFieldValue("resolve_difference", value === RESOLVE_DIFFERENCE);
    setFieldValue("pay_partially", value === PAY_PARTIALLY);

    const transactionDate = transaction.date
      ? dayjs(transaction.date).format(DISPLAY_DATE)
      : "";

    if (value === PAY_PARTIALLY) {
      setFieldValue("transactions", [
        {
          ...initialTransaction,
          id: randomBytes(10).toString("hex"),
          date: transactionDate,
        },
      ]);
    }

    if (value === RESOLVE_DIFFERENCE) {
      const exchangeGainOrLoss =
        pendingAmountWithoutManual > 0 ? "Exchange Loss" : "Exchange Gain";
      const incomeOrExpense =
        pendingAmountWithoutManual > 0 ? "Expense" : "Income";

      const defaultCategory = isAnySelectedBillMultiCurrency
        ? exchangeLossOrGain
        : incomeCategory;

      const vendorDefaults =
        selectedBills.length === 1
          ? { merchant: selectedBills[0]?.entity_merchant_data?.uuid || "" }
          : {};

      setFieldValue("transactions", [
        {
          ...initialTransaction,
          ...vendorDefaults,
          id: randomBytes(10).toString("hex"),
          date: transactionDate,
          description:
            defaultCategory?.name ||
            (isAnySelectedBillMultiCurrency
              ? exchangeGainOrLoss
              : incomeOrExpense),
          amount: pendingAmount,
        },
      ]);
    }
  };

  let resolveValue;
  if (formikValues.resolve_difference) resolveValue = RESOLVE_DIFFERENCE;
  if (formikValues.pay_partially) resolveValue = PAY_PARTIALLY;

  const columns = useMemo(
    () => [
      createColumn.display({
        id: "select",
        size: 1,
        enableSorting: false,
        header: () => <></>,
        cell: ({ row }) => {
          const isDisabled = !row.getCanSelect();
          return (
            <ConditionalToolTip
              condition={
                isDisabled
                  ? "The total amount of selected bills has already exceeded the transaction amount"
                  : false
              }
            >
              <span>
                <Checkbox
                  checked={row.getIsSelected()}
                  disabled={isDisabled}
                  indeterminate={row.getIsSomeSelected()}
                  key={row.id}
                />
              </span>
            </ConditionalToolTip>
          );
        },
      }),
      billColumns.bill_date(16),
      billColumns.bill_number(16),
      billColumns.entity_merchant_data(26),
      billColumns.due_balance(16),
    ],
    []
  );

  const table = useReactTable({
    data: bills,
    columns,
    state: {
      rowSelection: selectedBills.reduce(
        (acc, { uuid }) => ({ ...acc, [uuid]: true }),
        {}
      ),
    },
    enableRowSelection: (row) => (row.getIsSelected() ? true : enableSelection),
    getRowId: (row) => row.uuid,
    getCoreRowModel: getCoreRowModel(),
    defaultColumn: { minSize: 5 },
    meta: {
      displayCurrency,
      setDisplayCurrency,
    },
  });

  const onRowClick = (row: Row<VendorBill>) => {
    if (!row.getCanSelect()) return;

    const isSelected = selectedBills.find((b) => b.uuid === row.original.uuid);

    if (isSelected) {
      setSelectedBills((prev) =>
        prev.filter((b) => b.uuid !== row.original.uuid)
      );
      return;
    }

    setSelectedBills((prev) => [...prev, row.original]);
  };

  const handleSearch = debounce((e: React.ChangeEvent<HTMLInputElement>) => {
    setFieldValue("search", e.target.value);
  });

  const appliedFilterCount = filterValues.START_DATE ? 1 : 0;
  const filterTitle =
    appliedFilterCount > 0 ? `Filters (${appliedFilterCount})` : "Filters";

  if (isLoading && !isSuccess) {
    return (
      <div className="t-w-full t-h-full t-flex t-justify-center t-items-center">
        <Loader size="small" />
      </div>
    );
  }

  return (
    <div className="t-flex t-flex-col t-gap-6">
      <div className="t-border-solid t-border t-border-neutral-0 t-py-4 t-px-5 t-bg-white t-flex t-gap-8 t-rounded-lg sm:t-grid sm:t-grid-cols-2 sm:t-gap-4 sm:t-px-4">
        <InfoItem label="Date">
          <span className="t-font-semibold">
            {formatDate(transaction.date)}
          </span>
        </InfoItem>
        <Divider orientation="vertical" classNames="sm:!t-hidden" />
        <InfoItem label="Source">
          <ToolTip text={transaction.from?.bank_account?.bank_brand?.name}>
            <Avatar
              src={transaction.from?.bank_account?.bank_brand?.logo_url}
              alt={transaction.from?.bank_account?.bank_brand?.name || "Bank"}
            />
          </ToolTip>
        </InfoItem>
        {transaction.merchant && (
          <>
            <Divider orientation="vertical" classNames="sm:!t-hidden" />
            <InfoItem label="To/From">{transaction.merchant}</InfoItem>
          </>
        )}
        <Divider orientation="vertical" classNames="sm:!t-hidden" />
        <div className="t-ml-auto sm:t-ml-0">
          <InfoItem label="Available amount to link">
            <div className="t-text-right t-font-semibold sm:t-text-left">
              <AmountSuperScript amount={Number(transaction.amount) || 0} />
            </div>
          </InfoItem>
        </div>
      </div>

      {resolvePartialPaymentStep ? (
        <div>
          <p className="t-m-0 t-text-body-sm t-mb-6 t-text-text-100">
            The available transaction amount of{" "}
            <span className="t-font-bold">
              <AmountSuperScript amount={Math.abs(transaction.amount)} />
            </span>{" "}
            {overpayment ? (
              <span>
                is{" "}
                <span className="t-font-bold">
                  <AmountSuperScript
                    amount={Math.abs(pendingAmountWithoutManual)}
                  />
                </span>{" "}
                less than the selected bill(s)
              </span>
            ) : (
              <span>
                exceeds the selected bill(s) by{" "}
                <span className="t-font-bold">
                  <AmountSuperScript
                    amount={Math.abs(pendingAmountWithoutManual)}
                  />
                </span>{" "}
              </span>
            )}
            {isAnySelectedBillMultiCurrency && (
              <span>. This difference may be due to exchange rate changes</span>
            )}
            . How would you like to handle the difference?
          </p>

          <RadioGroup.Root
            value={resolveValue}
            name="resolve_difference"
            onValueChange={onResolveValueChange}
          >
            <RadioGroup.Content className="t-flex t-flex-col t-gap-5">
              <RadioCard value={RESOLVE_DIFFERENCE}>
                <div className="t-text-body-sm t-flex t-flex-col t-gap-0.5">
                  <span>Resolve the difference</span>
                  {isAnySelectedBillMultiCurrency ? (
                    <p className="t-text-text-30 t-m-0 t-italic t-text-body-italic sm:t-text-body-sm-italic">
                      Record the difference as a foreign exchange gain/loss or
                      discount/income if the difference is due to other reasons.
                    </p>
                  ) : (
                    <p className="t-text-text-30 t-m-0 t-italic t-text-body-italic sm:t-text-body-sm-italic">
                      Record the difference as{" "}
                      {overpayment ? "an income" : "an expense"}.
                    </p>
                  )}
                </div>
              </RadioCard>
              {formikValues.resolve_difference && (
                <div className="t-border-solid t-border-neutral-10 t-border t-pt-1 t-p-3 t-rounded-md">
                  <DiscountTransactions
                    type={FINANCIAL_RECORD_TYPE.BILL}
                    pendingAmount={pendingAmount}
                    selectedBills={selectedBills}
                  />
                </div>
              )}
              <RadioCard value={PAY_PARTIALLY}>
                <div className="t-text-body-sm t-flex t-flex-col t-gap-0.5">
                  <span>
                    {overpayment
                      ? "Partially link to bill(s)"
                      : "Mark as Partially Paid"}
                  </span>
                  <p className="t-text-text-30 t-m-0 t-italic t-text-body-italic sm:t-text-body-sm-italic">
                    Apply the {overpayment ? "transaction" : "bill"} amount
                    only. The remaining{" "}
                    <AmountSuperScript
                      amount={Math.abs(pendingAmountWithoutManual)}
                    />{" "}
                    {overpayment
                      ? "on the bill(s) will stay unpaid."
                      : "will stay unallocated as 'Unapplied Cash Bill Payment Expense'."}
                  </p>
                </div>
              </RadioCard>
            </RadioGroup.Content>
          </RadioGroup.Root>
        </div>
      ) : (
        <>
          <SuggestedBillsAccordion
            transactionId={transaction.uuid}
            selectedBills={selectedBills}
            setSelectedBills={setSelectedBills}
            enableSelection={enableSelection}
          />

          <div className="t-flex t-gap-2 t-flex-wrap">
            <Search
              className="t-w-[12%] focus:t-w-2/6 t-transition-all sm:t-w-auto sm:t-min-w-0 sm:t-flex-1 sm:focus:t-w-auto"
              placeholder="Search..."
              onChange={handleSearch}
            />
            <Filter.Root
              withWrapper
              defaultValue="DATE"
              title={filterTitle}
              capsule={
                <>
                  {filterValues.START_DATE && (
                    <span className="sm:t-hidden">
                      <Chip
                        onRemove={() => {
                          updateFilter("START_DATE", "");
                          updateFilter("END_DATE", "");
                          updateFilter("SELECT_PERIOD", "");
                        }}
                        isActive
                        filterType="DATE"
                      >
                        From: {formatDate(filterValues.START_DATE)} To:{" "}
                        {formatDate(filterValues.END_DATE)}
                      </Chip>
                    </span>
                  )}
                </>
              }
            >
              <Filter.Portal>
                <Filter.List>
                  <Filter.ListItem value="DATE">Date</Filter.ListItem>
                </Filter.List>
                <Filter.Body value="DATE" block>
                  <BooksDateFilter
                    values={filterValues}
                    updateFilter={updateFilter}
                  />
                </Filter.Body>
              </Filter.Portal>
            </Filter.Root>
            <div className="t-ml-auto">
              <PaginationHandler
                page_num={currentPage}
                per_page={paginationData.itemsPerPage}
                total_count={paginationData.totalItemCount}
                total_pages={paginationData.totalPage}
                onPageNumChange={setCurrentPage}
              />
            </div>
          </div>

          {bills.length === 0 ? (
            <div className="t-w-full t-flex t-flex-col t-justify-center t-items-center t-py-12 t-gap-2">
              <img src={EmptyInvoices} alt="EmptyBills" />
              <div className="t-text-body-sm t-text-text-30 sm:t-text-center">
                There are no bills that are unpaid and not yet linked
              </div>
            </div>
          ) : (
            <TableUI
              table={table}
              onRowClick={onRowClick}
              moreRows={
                <Table.Row className="!t-border-0">
                  <Table.Cell colSpan={3} />
                  <Table.Cell className="t-text-subtext t-text-text-60">
                    Amount Pending
                  </Table.Cell>
                  <Table.Cell className="t-text-right t-text-subtext t-text-text-60">
                    <AnimatedNumbers amount={Number(pendingAmount) || 0} />
                  </Table.Cell>
                </Table.Row>
              }
            />
          )}
        </>
      )}
    </div>
  );
};

export const LinkInkleBillModal = ({
  transactionId,
  isOpen,
  close,
}: { transactionId: string } & ModalProps) => {
  const entityId = useCurrentEntityId();
  const groupId = useCurrentGroupContext().uuid;
  const { alertToast, successToast } = useToast();
  const [markBillPaid, { isLoading }] = useMapInkleTransactionToBillMutation();
  const [selectedBills, setSelectedBills] = useState<VendorBill[]>([]);

  const {
    data: transaction,
    isLoading: isTransactionLoading,
    isSuccess: isTransactionSuccess,
  } = useGetTransactionDataQuery(
    { entityId, transactionId, groupId },
    { skip: !transactionId || !entityId }
  );

  const { chartOfAccounts = [] } = useChartOfAccounts({
    hiddenCategoryTypes: ["BANK_ACCOUNT"],
  });
  const withheldTaxPayableCategory = flattenTypes({
    accounts: chartOfAccounts,
  }).find(({ identifier }) => identifier === WITHHELD_TAX_PAYABLE_IDENTIFIER);

  const availableAmount = Math.abs(
    transaction?.available_amount || transaction?.transaction.amount || 0
  );

  const totalBillAmount = selectedBills.reduce(
    (acc, bill) => acc + getUsdPendingAmount(bill),
    0
  );

  const initialTransactions = useMemo(
    () => [
      {
        id: randomBytes(10).toString("hex"),
        ...initialTransaction,
      },
    ],
    []
  );

  const initialValues: LinkedBillsFormikValues = {
    search: "",
    transactions: initialTransactions,
    resolve_difference: false,
    pay_partially: false,
  };

  const partialPaymentStep = useModal();

  const onSubmit = async (values: {
    pay_partially?: boolean;
    resolve_difference?: boolean;
    transactions: InternalTransaction[];
  }) => {
    try {
      const entityBillIds = selectedBills.map(({ uuid }) => uuid).join(",");

      const partiallyPaying = totalBillAmount < availableAmount;
      const overpayment = totalBillAmount > availableAmount;

      // Bill amounts are always negative on the backend (expense/payable
      // convention), regardless of whether the txn over- or under-covers
      // the selected bill(s) -- unlike invoices, which use positive amounts.
      const transactions = values.resolve_difference
        ? values.transactions.map(
            ({ amount, description, category, merchant, withheldBillId }) => ({
              amount: overpayment ? -amount : amount,
              description,
              category_id: category,
              merchant_id: merchant,
              bill_ids: withheldBillId ? [withheldBillId] : undefined,
            })
          )
        : null;

      if (!partialPaymentStep.isOpen && (partiallyPaying || overpayment)) {
        return partialPaymentStep.open();
      }

      await markBillPaid({
        entityId,
        entityBillIds,
        transactionId,
        ...(transactions && { transactions }),
      }).unwrap();

      successToast({
        message:
          values.pay_partially || values.resolve_difference
            ? "Partial payment recorded successfully"
            : `Transaction linked to ${pluralize(
                selectedBills.length,
                "bill",
                "bills"
              )}`,
      });
      close();
    } catch (error) {
      alertToast({
        message: (error as BackendError)?.data?.error?.message,
      });
    }
  };

  return (
    <Formik onSubmit={onSubmit} initialValues={initialValues}>
      {({ isSubmitting, submitForm, setFieldValue, values }) => {
        const hasUnselectedWithheldBill =
          selectedBills.length > 1 &&
          values.transactions?.some(
            (currentTransaction) =>
              currentTransaction.category ===
                withheldTaxPayableCategory?.uuid &&
              !currentTransaction.withheldBillId
          );

        // The resolve-difference line items must fully cover the gap
        // between the transaction amount and the selected bill(s) total.
        const transactionsTotalAmount = (values.transactions || []).reduce(
          (sum, currentTransaction) =>
            sum + (Number(currentTransaction.amount) || 0),
          0
        );
        const isDifferenceUnresolved =
          Boolean(values.resolve_difference) &&
          roundToFixedDecimal({
            numberToRound:
              availableAmount - totalBillAmount - transactionsTotalAmount,
          }) !== 0;

        const isResolveDifferenceValid = values.resolve_difference
          ? linkInkleSchema.isValidSync(values)
          : true;

        return (
          <Form>
            <Modal.Root open={isOpen} onOpenChange={close}>
              <Modal.Content size="xxl" useCustomOverlay>
                <Modal.Header className="sm:t-pl-4">
                  <Modal.Title>Link to bills</Modal.Title>
                  <Modal.Close disabled={isLoading} />
                </Modal.Header>
                <Modal.Body className="sm:t-px-4">
                  <Async.Root
                    isLoading={isTransactionLoading}
                    isSuccess={isTransactionSuccess}
                    isEmpty={false}
                    customLoader={MODAL_BODY_LOADER}
                  >
                    <Async.Success>
                      {transaction && (
                        <LinkInkleBillBody
                          transaction={transaction}
                          selectedBills={selectedBills}
                          setSelectedBills={setSelectedBills}
                          resolvePartialPaymentStep={partialPaymentStep.isOpen}
                        />
                      )}
                    </Async.Success>
                  </Async.Root>
                </Modal.Body>
                <Modal.FooterButtonGroup>
                  {partialPaymentStep.isOpen ? (
                    <Button
                      customType="secondary"
                      onClick={() => {
                        partialPaymentStep.close();
                        setFieldValue("transactions", initialTransactions);
                        setFieldValue("resolve_difference", false);
                        setFieldValue("pay_partially", false);
                      }}
                    >
                      Back
                    </Button>
                  ) : (
                    <Modal.RawClose asChild>
                      <Button>Cancel</Button>
                    </Modal.RawClose>
                  )}
                  <Button
                    customType="primary"
                    isLoading={isSubmitting || isLoading}
                    disabled={
                      isSubmitting ||
                      isLoading ||
                      selectedBills.length === 0 ||
                      hasUnselectedWithheldBill ||
                      isDifferenceUnresolved ||
                      !isResolveDifferenceValid ||
                      (partialPaymentStep.isOpen &&
                        !values.pay_partially &&
                        !values.resolve_difference)
                    }
                    onClick={submitForm}
                  >
                    Proceed
                  </Button>
                </Modal.FooterButtonGroup>
              </Modal.Content>
            </Modal.Root>
          </Form>
        );
      }}
    </Formik>
  );
};
